// TEMP: Question Pool Upgrade — remove after full migration.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';

import PageLayout from 'layouts/PageLayout';
import {
  checkQuestionPoolCourses,
  getQuestionPoolPoolFlags,
  getQuestionPoolVersionGroups,
  syncQuestionPoolExports,
  upgradeQuestionPoolCourses,
} from 'http/questionPoolUpgrade';

const PAGE_SIZE = 5;
const POOL_FILTER_NO_POOL = 'no_pool';
const POOL_FILTER_ALL = 'all';

const useStyles = makeStyles(theme => ({
  section: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  controls: {
    display: 'flex',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  groupsWrap: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    maxHeight: 420,
    overflow: 'auto',
    marginBottom: theme.spacing(2),
  },
  groupRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: theme.spacing(1),
    border: `1px solid ${theme.palette.grey[300]}`,
    borderRadius: 4,
    padding: theme.spacing(1),
  },
  courseColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(0.5),
    paddingLeft: theme.spacing(4),
  },
  statusChip: {
    marginRight: theme.spacing(0.5),
  },
  filterControl: {
    minWidth: 160,
  },
  messageCell: {
    maxWidth: 420,
    whiteSpace: 'pre-wrap',
  },
}));

const statusColor = status => {
  switch (status) {
    case 'PASS':
    case 'UPGRADE_SUCCESS':
      return 'primary';
    case 'SKIP_ALREADY_POOL':
      return 'default';
    case 'BLOCKED_IN_PROGRESS':
    case 'BLOCKED_NOT_EXPORTED':
    case 'UPGRADE_FAILED':
    case 'ERROR':
      return 'secondary';
    default:
      return 'default';
  }
};

const isAlreadyPoolItem = item =>
  Boolean(item?.alreadyPool) || item?.status === 'SKIP_ALREADY_POOL';

const QuestionPoolUpgrade = () => {
  const classes = useStyles();
  const { t } = useTranslation('common');

  const [groups, setGroups] = useState([]);
  const [groupsTotal, setGroupsTotal] = useState(0);
  const [poolFilter, setPoolFilter] = useState(POOL_FILTER_NO_POOL);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [poolFlagsByCourseId, setPoolFlagsByCourseId] = useState(() => new Map());
  const [checkResult, setCheckResult] = useState(null);
  const [upgradeResult, setUpgradeResult] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPoolFlags, setLoadingPoolFlags] = useState(false);
  const [loadingCheck, setLoadingCheck] = useState(false);
  const [loadingUpgrade, setLoadingUpgrade] = useState(false);
  const [syncingCourseId, setSyncingCourseId] = useState(null);
  const [error, setError] = useState('');
  const [syncMessage, setSyncMessage] = useState('');
  const [groupsPage, setGroupsPage] = useState(0);

  const loadGroups = useCallback(
    async (page = 0, filter = poolFilter) => {
      setLoadingGroups(true);
      setError('');
      try {
        const data = await getQuestionPoolVersionGroups({
          page,
          limit: PAGE_SIZE,
          poolFilter: filter,
        });
        setGroups(Array.isArray(data?.items) ? data.items : []);
        setGroupsTotal(Number(data?.total) || 0);
        setGroupsPage(Number.isInteger(data?.page) ? data.page : page);
        setPoolFlagsByCourseId(new Map());
      } catch (err) {
        setError(
          err?.response?.data?.message || err?.message || 'Failed to load groups',
        );
        setGroups([]);
        setGroupsTotal(0);
        setGroupsPage(0);
        setPoolFlagsByCourseId(new Map());
      } finally {
        setLoadingGroups(false);
      }
    },
    [poolFilter],
  );

  useEffect(() => {
    loadGroups(0, poolFilter);
  }, [loadGroups, poolFilter]);

  const onChangePoolFilter = event => {
    const next = event.target.value === POOL_FILTER_ALL
      ? POOL_FILTER_ALL
      : POOL_FILTER_NO_POOL;
    setPoolFilter(next);
    setSelectedCourseIds([]);
    setCheckResult(null);
    setUpgradeResult(null);
    setSyncMessage('');
  };

  const passCourseIds = useMemo(() => {
    if (!checkResult?.items || !selectedCourseIds.length) return [];
    return checkResult.items
      .filter(item => item.canUpgrade && item.status === 'PASS')
      .filter(item => selectedCourseIds.includes(item.moodleCourseId))
      .map(item => item.moodleCourseId);
  }, [checkResult, selectedCourseIds]);

  const checkStatusByCourseId = useMemo(() => {
    const map = new Map();
    (checkResult?.items || []).forEach(item => {
      map.set(item.moodleCourseId, item);
    });
    return map;
  }, [checkResult]);

  const resolveCoursePoolState = useCallback(
    courseId => {
      const checked = checkStatusByCourseId.get(courseId);
      if (checked) return checked;
      return poolFlagsByCourseId.get(courseId) || null;
    },
    [checkStatusByCourseId, poolFlagsByCourseId],
  );

  const checkItems = checkResult?.items || [];
  const upgradeItems = upgradeResult?.items || [];

  const visibleCourseIds = useMemo(() => {
    const ids = [];
    groups.forEach(group => {
      (group.courses || []).forEach(course => {
        if (course.moodleCourseId) ids.push(course.moodleCourseId);
      });
    });
    return ids;
  }, [groups]);

  useEffect(() => {
    let cancelled = false;
    if (!visibleCourseIds.length) {
      return undefined;
    }

    (async () => {
      setLoadingPoolFlags(true);
      try {
        const data = await getQuestionPoolPoolFlags(visibleCourseIds);
        if (cancelled) return;
        setPoolFlagsByCourseId(prev => {
          const next = new Map(prev);
          (data?.items || []).forEach(item => {
            if (item?.moodleCourseId) {
              next.set(item.moodleCourseId, item);
            }
          });
          visibleCourseIds.forEach(id => {
            if (!next.has(id)) {
              next.set(id, {
                moodleCourseId: id,
                alreadyPool: false,
              });
            }
          });
          return next;
        });
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              'Failed to load pool flags',
          );
        }
      } finally {
        if (!cancelled) setLoadingPoolFlags(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- refetch only when visible page course set changes
  }, [visibleCourseIds.join(',')]);

  const onChangeGroupsPage = (_e, page) => {
    loadGroups(page, poolFilter);
  };

  const toggleCourse = courseId => {
    const status = resolveCoursePoolState(courseId);
    if (isAlreadyPoolItem(status)) return;
    setSelectedCourseIds(prev =>
      prev.includes(courseId)
        ? prev.filter(id => id !== courseId)
        : [...prev, courseId],
    );
  };

  const toggleGroup = group => {
    const ids = (group.courses || [])
      .map(course => course.moodleCourseId)
      .filter(id => !isAlreadyPoolItem(resolveCoursePoolState(id)));
    if (!ids.length) return;
    const allSelected = ids.every(id => selectedCourseIds.includes(id));
    if (allSelected) {
      setSelectedCourseIds(prev => prev.filter(id => !ids.includes(id)));
    } else {
      setSelectedCourseIds(prev => Array.from(new Set([...prev, ...ids])));
    }
  };

  const selectAllVisible = () => {
    const selectable = visibleCourseIds.filter(
      id => !isAlreadyPoolItem(resolveCoursePoolState(id)),
    );
    setSelectedCourseIds(prev => Array.from(new Set([...prev, ...selectable])));
  };

  const clearSelection = () => {
    setSelectedCourseIds([]);
  };

  const onCheck = async () => {
    if (!selectedCourseIds.length) return;
    setLoadingCheck(true);
    setError('');
    setSyncMessage('');
    setUpgradeResult(null);
    try {
      const data = await checkQuestionPoolCourses(selectedCourseIds);
      setCheckResult(data);
      setPoolFlagsByCourseId(prev => {
        const next = new Map(prev);
        (data?.items || []).forEach(item => {
          if (item?.moodleCourseId) {
            next.set(item.moodleCourseId, {
              moodleCourseId: item.moodleCourseId,
              alreadyPool: isAlreadyPoolItem(item),
              shortname: item.shortname,
              quizid: item.quizid,
            });
          }
        });
        return next;
      });
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Check failed');
      setCheckResult(null);
    } finally {
      setLoadingCheck(false);
    }
  };

  const onSyncRowExports = async item => {
    if (!item?.moodleCourseId) return;
    const attemptIds = Array.isArray(item.unexportedAttemptIds)
      ? item.unexportedAttemptIds
      : [];
    setSyncingCourseId(item.moodleCourseId);
    setError('');
    setSyncMessage('');
    try {
      const data = await syncQuestionPoolExports({
        moodleCourseId: item.moodleCourseId,
        moodleAttemptIds: attemptIds,
      });
      setSyncMessage(
        data?.message ||
          `Enqueued: ${data?.enqueued || 0}, skipped: ${
            data?.skippedDuplicate || 0
          }`,
      );
      const refreshIds = (checkResult?.items || []).map(
        row => row.moodleCourseId,
      );
      const ids =
        refreshIds.length > 0 ? refreshIds : [item.moodleCourseId];
      const refreshed = await checkQuestionPoolCourses(ids);
      setCheckResult(refreshed);
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Sync failed');
    } finally {
      setSyncingCourseId(null);
    }
  };

  const onUpgrade = async () => {
    if (!passCourseIds.length) return;
    setLoadingUpgrade(true);
    setError('');
    try {
      const data = await upgradeQuestionPoolCourses(passCourseIds, 20);
      setUpgradeResult(data);
      const idsToRefresh = Array.from(
        new Set([...passCourseIds, ...selectedCourseIds]),
      );
      const refreshed = await checkQuestionPoolCourses(
        idsToRefresh.length ? idsToRefresh : passCourseIds,
      );
      setCheckResult(refreshed);
      setPoolFlagsByCourseId(prev => {
        const next = new Map(prev);
        (refreshed?.items || []).forEach(item => {
          if (item?.moodleCourseId) {
            next.set(item.moodleCourseId, {
              moodleCourseId: item.moodleCourseId,
              alreadyPool: isAlreadyPoolItem(item),
              shortname: item.shortname,
              quizid: item.quizid,
            });
          }
        });
        return next;
      });
      const upgradedPoolIds = new Set(
        (refreshed?.items || [])
          .filter(isAlreadyPoolItem)
          .map(item => item.moodleCourseId),
      );
      setSelectedCourseIds(prev => prev.filter(id => !upgradedPoolIds.has(id)));
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Upgrade failed');
    } finally {
      setLoadingUpgrade(false);
    }
  };

  return (
    <PageLayout title={t('questionPoolUpgrade.pageTitle')}>
      <Paper className={classes.section}>
        <Typography variant="body2" gutterBottom>
          {t('questionPoolUpgrade.description')}
        </Typography>
        <div className={classes.controls}>
          <FormControl
            variant="outlined"
            size="small"
            className={classes.filterControl}
          >
            <InputLabel id="question-pool-filter-label">
              {t('questionPoolUpgrade.poolFilter')}
            </InputLabel>
            <Select
              labelId="question-pool-filter-label"
              label={t('questionPoolUpgrade.poolFilter')}
              value={poolFilter}
              onChange={onChangePoolFilter}
              disabled={loadingGroups}
            >
              <MenuItem value={POOL_FILTER_NO_POOL}>
                {t('questionPoolUpgrade.poolFilterNoPool')}
              </MenuItem>
              <MenuItem value={POOL_FILTER_ALL}>
                {t('questionPoolUpgrade.poolFilterAll')}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => loadGroups(0, poolFilter)}
            disabled={loadingGroups}
          >
            {t('questionPoolUpgrade.refreshGroups')}
          </Button>
          <Button variant="outlined" onClick={selectAllVisible}>
            {t('questionPoolUpgrade.selectAll')}
          </Button>
          <Button variant="outlined" onClick={clearSelection}>
            {t('questionPoolUpgrade.clearSelection')}
          </Button>
          {loadingPoolFlags && (
            <Typography variant="body2" color="textSecondary">
              {t('questionPoolUpgrade.loadingPoolFlags')}
            </Typography>
          )}
        </div>

        <div className={classes.groupsWrap}>
          {groups.map(group => {
            const ids = (group.courses || []).map(c => c.moodleCourseId);
            const selectableIds = ids.filter(
              id => !isAlreadyPoolItem(resolveCoursePoolState(id)),
            );
            const selectedCount = selectableIds.filter(id =>
              selectedCourseIds.includes(id),
            ).length;
            const knownStatuses = ids
              .map(id => resolveCoursePoolState(id))
              .filter(Boolean);
            const isFullyPool =
              ids.length > 0 &&
              knownStatuses.length === ids.length &&
              knownStatuses.every(isAlreadyPoolItem);
            return (
              <div key={group.key} className={classes.groupRow}>
                <FormControlLabel
                  control={
                    <Checkbox
                      color="primary"
                      checked={
                        selectableIds.length > 0 &&
                        selectedCount === selectableIds.length
                      }
                      indeterminate={
                        selectedCount > 0 &&
                        selectedCount < selectableIds.length
                      }
                      onChange={() => toggleGroup(group)}
                      disabled={isFullyPool || !selectableIds.length}
                    />
                  }
                  label={`${group.moduleCode} / ${group.version}.${group.revision} / ${group.language} (${group.courseCount})${
                    isFullyPool ? ' — SKIP_ALREADY_POOL' : ''
                  }`}
                />
                <div className={classes.courseColumn}>
                  {(group.courses || []).map(course => {
                    const status = resolveCoursePoolState(course.moodleCourseId);
                    const courseAlreadyPool = isAlreadyPoolItem(status);
                    return (
                      <FormControlLabel
                        key={course.moodleCourseId}
                        control={
                          <Checkbox
                            color="primary"
                            checked={
                              courseAlreadyPool ||
                              selectedCourseIds.includes(course.moodleCourseId)
                            }
                            onChange={() => toggleCourse(course.moodleCourseId)}
                            disabled={courseAlreadyPool}
                          />
                        }
                        label={`${
                          course.courseName ||
                          course.shortname ||
                          course.moodleCourseId
                        }${
                          course.shortname && course.courseName
                            ? ` [${course.shortname}]`
                            : ''
                        }${
                          courseAlreadyPool ? ' — SKIP_ALREADY_POOL' : ''
                        }`}
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
          {!groups.length && !loadingGroups && (
            <Typography color="textSecondary">
              {t('questionPoolUpgrade.noGroups')}
            </Typography>
          )}
        </div>
        {groupsTotal > 0 && (
          <TablePagination
            component="div"
            count={groupsTotal}
            page={groupsPage}
            onChangePage={onChangeGroupsPage}
            rowsPerPage={PAGE_SIZE}
            rowsPerPageOptions={[PAGE_SIZE]}
            onChangeRowsPerPage={() => {}}
          />
        )}

        <div className={classes.controls}>
          <Button
            variant="contained"
            color="primary"
            onClick={onCheck}
            disabled={!selectedCourseIds.length || loadingCheck}
          >
            {t('questionPoolUpgrade.check')}
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={onUpgrade}
            disabled={!passCourseIds.length || loadingUpgrade}
          >
            {t('questionPoolUpgrade.upgrade')}
          </Button>
          <Typography variant="body2">
            {t('questionPoolUpgrade.selectedCount', {
              count: selectedCourseIds.length,
            })}
          </Typography>
          {checkResult && (
            <Typography variant="body2">
              {t('questionPoolUpgrade.checkSummary', {
                pass: checkResult.passCount || 0,
                blocked: checkResult.blockedCount || 0,
                skip: checkResult.skipCount || 0,
              })}
            </Typography>
          )}
          {syncMessage && (
            <Typography variant="body2" color="textSecondary">
              {syncMessage}
            </Typography>
          )}
        </div>

        {error && (
          <Typography color="error" gutterBottom>
            {error}
          </Typography>
        )}
      </Paper>

      {checkItems.length > 0 && (
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            {t('questionPoolUpgrade.checkResults')}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('questionPoolUpgrade.courseId')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.shortname')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.status')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.finished')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.exported')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.inProgress')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.message')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.action')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {checkItems.map(item => (
                  <TableRow key={item.moodleCourseId}>
                    <TableCell>{item.moodleCourseId}</TableCell>
                    <TableCell>{item.shortname}</TableCell>
                    <TableCell>
                      <Chip
                        className={classes.statusChip}
                        size="small"
                        color={statusColor(item.status)}
                        label={item.status}
                      />
                    </TableCell>
                    <TableCell>{item.finishedCount}</TableCell>
                    <TableCell>{item.exportedCount}</TableCell>
                    <TableCell>{item.inProgressCount}</TableCell>
                    <TableCell className={classes.messageCell}>
                      {item.message}
                    </TableCell>
                    <TableCell>
                      {item.status === 'BLOCKED_NOT_EXPORTED' && (
                        <Button
                          size="small"
                          variant="outlined"
                          color="primary"
                          onClick={() => onSyncRowExports(item)}
                          disabled={
                            syncingCourseId === item.moodleCourseId ||
                            !(item.unexportedAttemptIds || []).length
                          }
                        >
                          {t('questionPoolUpgrade.syncExports')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {upgradeItems.length > 0 && (
        <Paper className={classes.section}>
          <Typography variant="h6" gutterBottom>
            {t('questionPoolUpgrade.upgradeResults')}
          </Typography>
          <Typography variant="body2" gutterBottom>
            {t('questionPoolUpgrade.upgradeSummary', {
              success: upgradeResult.successCount || 0,
              failed: upgradeResult.failedCount || 0,
              skipped: upgradeResult.skippedCount || 0,
            })}
          </Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('questionPoolUpgrade.courseId')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.shortname')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.status')}</TableCell>
                  <TableCell>{t('questionPoolUpgrade.usersReset')}</TableCell>
                  <TableCell>
                    {t('questionPoolUpgrade.attemptsDeleted')}
                  </TableCell>
                  <TableCell>{t('questionPoolUpgrade.message')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {upgradeItems.map(item => (
                  <TableRow key={`${item.moodleCourseId}-${item.status}`}>
                    <TableCell>{item.moodleCourseId}</TableCell>
                    <TableCell>{item.shortname}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        color={statusColor(item.status)}
                        label={item.status}
                      />
                    </TableCell>
                    <TableCell>{item.usersReset}</TableCell>
                    <TableCell>{item.attemptsDeleted}</TableCell>
                    <TableCell className={classes.messageCell}>
                      {item.message}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </PageLayout>
  );
};

export default QuestionPoolUpgrade;
